import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Button, Flex } from '~/ui/components';
import { useAwaitControl } from 'react-redux-await-control';
import { getTicketAssessment, getTicketMessages } from '~/store/tickets/actions';
import { LoadingSpinner } from '~/ui/assets/icons';
import { LoadingContent, Title } from './Conversation.styled';

import { MessageType, RenderConversation } from './RenderConvesation';

type ConversationType = {
  conversationId: string;
};

function Conversation({ conversationId }: ConversationType) {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [lastPageToken, setLastPageToken] = useState('');

  const ticketMessagesControl = useAwaitControl(getTicketMessages);
  const ticketAssessmentControl = useAwaitControl(getTicketAssessment);

  const ticketAssessmentLoading = ticketAssessmentControl.isRunning();

  const ticketMessages = ticketMessagesControl.result(conversationId);
  const ticketMessagesLoading = ticketMessagesControl.isRunning(conversationId);
  const ticketMessagesFail = ticketMessagesControl.hasFailure(conversationId);

  const getNewMessages = useCallback(
    (ticketMessages) => {
      ticketMessagesControl.start(
        {
          conversationId,
          pageToken: lastPageToken ?? ticketMessages?.meta?.nextPageToken,
        },
        { actionId: conversationId },
      );
    },
    [lastPageToken, conversationId],
  );

  useEffect(() => {
    setMessages([]);

    ticketMessagesControl.start({ conversationId }, { actionId: conversationId });
  }, [conversationId]);

  useEffect(() => {
    if (ticketMessages?.messages) {
      setMessages((prev) => {
        // Cria um conjunto de índices das mensagens existentes
        const existingIndices = new Set(prev.map((message) => message.index));

        // Filtra as novas mensagens para incluir apenas aquelas que não estão já presentes
        const newMessages = ticketMessages.messages.filter((message) => !existingIndices.has(message.index));

        return [...prev, ...newMessages];
      });
    }
  }, [ticketMessages]);

  useEffect(() => {
    if (ticketMessages && !ticketMessagesFail && ticketMessages?.meta?.nextPageToken && !ticketAssessmentLoading) {
      setLastPageToken(ticketMessages.meta.nextPageToken);
    }

    if (ticketAssessmentLoading) {
      setLastPageToken('');
    }
  }, [ticketMessages, ticketMessagesFail, ticketAssessmentLoading]);

  if (ticketMessagesLoading && !messages.length) {
    return (
      <Alert
        status="neutral"
        hideIcon
        fullWidth
        renderValue={
          <LoadingContent>
            <LoadingSpinner width={20} height={20} />
            <Title>Carregando mensagens...</Title>
          </LoadingContent>
        }
      />
    );
  }

  if (!ticketMessagesLoading && !messages.length) {
    return (
      <Alert
        label="Não foi possível exibir o histórico do atendimento, ticket não possuí conversa associada."
        status="neutral"
        fullWidth
      />
    );
  }

  return (
    <>
      <RenderConversation messages={messages} conversationId={conversationId} />
      <Flex justify="center" pv={16} hidden={ticketMessages?.meta?.lastPage}>
        <Button size="sm" variant="text" onClick={() => getNewMessages(ticketMessages)} loading={ticketMessagesLoading}>
          Carregar mais
        </Button>
      </Flex>
    </>
  );
}

export default Conversation;
